class ProductSizeChart extends HTMLElement {
  connectedCallback() {
    this.openButton = this.querySelector('[data-size-chart-open]');
    this.closeButton = this.querySelector('[data-size-chart-close]');
    this.dialog = this.querySelector('[data-size-chart-dialog]');
    this.tableContainer = this.querySelector('[data-size-chart-table]');
    this.source = this.querySelector('[data-size-chart-source]');

    if (!this.openButton || !this.closeButton || !this.dialog || !this.tableContainer || !this.source) {
      this.hidden = true;
      return;
    }

    this.#renderTable();

    if (this.hidden) return;

    this.openButton.setAttribute('aria-expanded', 'false');
    this.openButton.addEventListener('click', this.#openDialog);
    this.closeButton.addEventListener('click', this.#closeDialog);
    this.dialog.addEventListener('click', this.#handleDialogClick);
    this.dialog.addEventListener('cancel', this.#handleCancel);
    this.dialog.addEventListener('close', this.#handleClose);
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.#openDialog);
    this.closeButton?.removeEventListener('click', this.#closeDialog);
    this.dialog?.removeEventListener('click', this.#handleDialogClick);
    this.dialog?.removeEventListener('cancel', this.#handleCancel);
    this.dialog?.removeEventListener('close', this.#handleClose);

    if (this.dialog?.open) {
      this.#unlockScroll();
    }
  }

  #renderTable() {
    const description = this.#getDescription();
    const rows = this.#extractTableRows(description);

    if (!rows || rows.length < 2 || !this.tableContainer) {
      this.hidden = true;
      return;
    }

    const [headerRow, ...bodyRows] = rows;
    const table = document.createElement('table');
    table.className = 'pdp-size-chart__table';

    const thead = document.createElement('thead');
    const headTr = document.createElement('tr');
    headerRow.forEach((cellText) => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = cellText;
      headTr.appendChild(th);
    });
    thead.appendChild(headTr);

    const tbody = document.createElement('tbody');
    bodyRows.forEach((row) => {
      const tr = document.createElement('tr');
      row.forEach((cellText) => {
        const td = document.createElement('td');
        td.textContent = cellText;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });

    table.append(thead, tbody);
    this.tableContainer.replaceChildren(table);
  }

  #getDescription() {
    try {
      return JSON.parse(this.source?.textContent || '""');
    } catch {
      return '';
    }
  }

  #extractTableRows(description) {
    if (!description) return null;

    const normalizedHtml = description
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/&nbsp;/gi, ' ');

    const temp = document.createElement('div');
    temp.innerHTML = normalizedHtml;

    const text = temp.textContent || temp.innerText || '';
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    let currentBlock = [];
    let bestBlock = [];

    lines.forEach((line) => {
      const pipeCount = (line.match(/\|/g) || []).length;

      if (pipeCount >= 2) {
        currentBlock.push(line);
        return;
      }

      if (currentBlock.length > bestBlock.length) {
        bestBlock = [...currentBlock];
      }
      currentBlock = [];
    });

    if (currentBlock.length > bestBlock.length) {
      bestBlock = [...currentBlock];
    }

    const rows = bestBlock
      .map((line) =>
        line
          .split('|')
          .map((cell) => cell.trim())
          .filter(Boolean)
      )
      .filter((row) => row.length > 1);

    if (rows.length < 2) return null;

    const bodyRows = [...rows];
    if (this.#isSeparatorRow(bodyRows[1])) {
      bodyRows.splice(1, 1);
    }

    return bodyRows.length > 1 ? bodyRows : null;
  }

  #isSeparatorRow(row) {
    if (!row) return false;

    return row.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s+/g, '')));
  }

  #openDialog = () => {
    if (!this.dialog || this.dialog.open) return;

    this.#lockScroll();
    this.dialog.showModal();
    this.openButton?.setAttribute('aria-expanded', 'true');
  };

  #closeDialog = () => {
    if (!this.dialog?.open) return;

    this.dialog.close();
  };

  #handleDialogClick = (event) => {
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    const rect = this.dialog.getBoundingClientRect();
    const clickedOutside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (clickedOutside) {
      this.#closeDialog();
    }
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.#closeDialog();
  };

  #handleClose = () => {
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.#unlockScroll();
  };

  #lockScroll() {
    this.previousScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.previousScrollY}px`;
  }

  #unlockScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (typeof this.previousScrollY === 'number') {
      window.scrollTo({ top: this.previousScrollY, behavior: 'instant' });
    }
  }
}

if (!customElements.get('product-size-chart')) {
  customElements.define('product-size-chart', ProductSizeChart);
}
